/**
 * Story 15.8 / 12.13 — pure framing math for the listing photos.
 *
 * The seller drags and zooms a fixed-aspect window over EVERY photo; the
 * release position is stored as a NORMALIZED focal point — the position of the
 * crop rectangle's CENTRE inside the source image — plus a zoom multiplier, in
 * `publicData.photoFocalPoints[<image uuid>] = { x, y, zoom }`.
 *
 * 🔴 12.13 — `photoFocalPoints` is the LISTING's framing. Its consumers are the
 * listing-page gallery hero, the wizard tiles and the Step 3 hero preview. The
 * AD's framing is a SEPARATE, SIBLING key owned by story 12-14; this module
 * neither writes nor reads it, and this key must NOT be generalised into a
 * multi-target container. The entry stays exactly three scalars.
 *
 * ⚠️ TRANSITIONALLY the ad renderer still reads THIS key for the cover photo
 * (15.8's shipped behaviour), so the ad's crop is APPROXIMATE until 12-14
 * ships. That is deliberate.
 *
 * 🔴 12.13 AC-4 — THE TARGET IS THE CALLER'S RESPONSIBILITY, AND OMITTING IT
 * THROWS. 15.8's helpers defaulted to the ad band, which made "framed against
 * the wrong target" a silent wrong-picture rather than a test failure. Every
 * helper below that takes a target now REQUIRES it and throws a named error
 * when it is absent or invalid — matching its tph-api twin `computeCoverCrop`.
 * SOURCE-side problems still degrade to `null`/a fallback, because an image
 * whose variants have not landed yet is a normal transient, not a programming
 * error. Targets are the programmer's responsibility; sources are the
 * network's.
 *
 * 🔴 CROSS-REPO CONTRACT (tph-api code review SF-4). The renderer's
 * `normalizeFocalPoint` REJECTS rather than clamps: a focal point that is not
 * two FINITE NUMBERS within [0,1] degrades to centre on BOTH the render side
 * and the re-render detector side, silently, with no seller-visible signal. A
 * type drift here (writing "0.35" instead of 0.35, or 1.0000000002 from float
 * noise) therefore makes the whole feature inert in production. Everything this
 * module writes is clamped to [0,1] and rounded to 4 decimals, and is always a
 * number. `pinnedFocalShape` exists so a test can assert exactly that.
 *
 * Everything here is pure: no DOM, no React, no I/O.
 */

// The ad's sharp photo band, from tph-api layout.js (980 x 742 at (110, 234) on
// the 1200 x 1200 canvas). The blurred backdrop crops at a DIFFERENT aspect
// (1200 x 735), which is why the renderer applies the same focal independently
// to each band.
//
// 🔴 12.13 — these constants are PRESERVED for the Step 6 (story 12-14) ad
// card, and they are NO LONGER the default target of anything. Step 3 frames
// against the LISTING's own aspect (`config.layout.listingImage`, 4/3 today),
// resolved through `useConfiguration()` and passed explicitly. The only Step 3
// use of the band is `maxZoomForSource`'s dual-target ceiling, which is
// deliberate: the renderer consumes the same stored zoom against 1.321, so a
// zoom safe at 4:3 but soft at 1.321 would ship a soft ad.
export const AD_BAND_WIDTH = 980;
export const AD_BAND_HEIGHT = 742;

// The blurred backdrop behind the window, from the same layout.js constants
// (1200 x 735 at (0, 234)). It crops at 1.633 where the sharp band crops at
// 1.321, so the SAME normalized focal has to be resolved against each target
// independently — the renderer does exactly that, and the preview must too.
export const AD_BACKDROP_WIDTH = 1200;
export const AD_BACKDROP_HEIGHT = 735;

// The ad canvas is 1200 x 1200 (layout.js:25) and the production backdrop blur
// is 50px against it, so a preview rendered `w` px wide needs `50 * w / 1200`.
export const AD_CANVAS_WIDTH = 1200;
export const AD_BACKDROP_BLUR_PX = 50;

/**
 * The backdrop blur for a preview of a given rendered width — TWO LEGS, and it
 * ALWAYS returns a blur.
 *
 * 🔴 IT LIVES HERE BECAUSE TWO SURFACES NEED IT. Step 6's `AdPreview` and step
 * 3's `YourAdCard` both draw the same blurred backdrop, and both were shipped
 * with `canvasWidth ? blur(px) : undefined` — where `undefined` meant NO BLUR
 * AT ALL on the first paint, and permanently on any mount that never measured.
 * Fixed in `AdPreview` on 2026-09-01 and found still live in `YourAdCard` by
 * the review that followed; putting the formula beside the constants it reads
 * is what stops the two copies drifting again (deferred-work #689, arity
 * coupling: one copy fixed, the other left wrong).
 *
 *   - MEASURED → `px`. Exact, and the steady state on every surface.
 *   - UNMEASURED → `cqw`. 1cqw is 1% of the nearest query container's inline
 *     size, so the caller's backdrop element MUST declare
 *     `container-type: inline-size` and MUST be `width: 100%` of the ad canvas.
 *     Both call sites satisfy that; if a third does not, `cqw` silently
 *     resolves against the VIEWPORT instead (measured: `blur(41.6667px)` at a
 *     1000px viewport) rather than failing.
 *
 * ⚠️ THE `cqw` LEG IS A FIRST-FRAME MEASURE, NOT A SUBSTITUTE FOR MEASURING.
 * Container query units are unsupported on `ios_saf 15.6-15.8` and
 * `and_uc 15.5`, both inside this app's production browserslist, where the
 * value is invalid and the declaration is dropped entirely. The value is an
 * inline style, so postcss never sees it and nothing can prefix or polyfill
 * it. Any surface that relies on `cqw` alone is unblurred on those browsers —
 * which is why both call sites measure.
 *
 * Both legs read the same two constants above: one number, written two ways.
 */
export const blurFilter = canvasWidth =>
  canvasWidth
    ? `blur(${(AD_BACKDROP_BLUR_PX * canvasWidth) / AD_CANVAS_WIDTH}px)`
    : `blur(${(AD_BACKDROP_BLUR_PX * 100) / AD_CANVAS_WIDTH}cqw)`;

/**
 * The canonical "not zoomed" multiplier. An absent `zoom` means exactly this,
 * everywhere, with no exceptions — every entry 15.8 wrote to staging is
 * `{x, y}` with no zoom, and every one of them must keep meaning what it means
 * today.
 */
export const NO_ZOOM = 1;

/**
 * A UX bound on how far in a seller may zoom, independent of how many pixels
 * the source has. Named rather than inlined so it is not read as a magic
 * number, and so the tph-api ZOOM_CEILING (100) can be seen to sit far above
 * it — that ceiling is a corrupt-data backstop, NOT a second product bound.
 */
export const MAX_ZOOM_CAP = 3;

/**
 * 🔴 12.13 AC-5 / AC-9 site (1) — THE SENTINEL IS THREE KEYS, AND THAT IS
 * LOAD-BEARING.
 *
 * Unset ⇒ centre, unzoomed. This default is never written eagerly — an absent
 * key IS the default, and only entries the seller actually moved are stored.
 *
 * Its tph-api twin is `adTemplateRenderer/focal.js`'s `CENTRE`, which gained
 * `zoom: 1` in the same story. Two failure modes make the third key mandatory
 * rather than tidy:
 *
 *   (a) THE MASS RE-RENDER STORM. Both sides of the edit-arm detector's
 *       comparison fall back to the sentinel, and `adRenderedState.focal` is
 *       `null` for every centre-framed listing by design. Teach the comparison
 *       about zoom while leaving the sentinel on two keys and you get
 *       `1 !== undefined` ⇒ a re-render enqueued for EVERY published listing on
 *       its first edit after deploy — each one a fresh `?v=<jobId>`, a new
 *       Airtable attachment id and a Dropbox re-upload.
 *   (b) THE FRAMING TOOL BREAKS IN ITS DEFAULT STATE. `readFocalPoint` returns
 *       this constant for every photo the seller has not yet framed — the
 *       default state of every photo on every draft — and it flows into
 *       `focalToInitialCroppedAreaPercentages`, which consumes `zoom`. A
 *       `zoom: undefined` yields NaN width/height ⇒ garbage
 *       `initialCroppedAreaPercentages` ⇒ the controlled cropper's restore
 *       contract fails on the most common path.
 *
 * Change the sentinel BEFORE the comparison, never after.
 */
export const CENTRE_FOCAL_POINT = Object.freeze({ x: 0.5, y: 0.5, zoom: NO_ZOOM });

// The drag hint + focal reticle appear only when the photo actually overflows
// the window.
//
// 🔴 THE THRESHOLD IS A FRACTION OF THE WINDOW, NOT AN ABSOLUTE.
//
// It used to be `OVERFLOW_THRESHOLD_PX = 2`, which was correct only while the
// sole caller (`YourAdCard`) passed the ad band's real pixels — 980 x 742. When
// `PhotoFramer` reused the helper it passed the LISTING's aspect UNITS instead
// (4 and 3, straight from `configLayout`'s '4/3'), and "2px" silently became 2
// units of a 4-wide window — 50% of its width, 67% of its height. A 3:2 or 16:9
// landscape overflows by 12–33% of the window, drags perfectly well, and was
// nonetheless gated OUT of showing the hint and the reticle.
//
// A helper whose behaviour depends on the caller's choice of units is the bug.
// Normalising the comparison to a FRACTION of the target makes the answer
// identical whether the caller passes 980 x 742 or 4 x 3.
//
// The value preserves what 2px meant at the band's scale, which is where the
// threshold was originally calibrated:
//
//   2px / 980px = 0.00204…  →  0.002 (0.2% of the window)
//
// It still rejects the degenerate case the original comment named: a 836 x 633
// source scales to 980 x 742.03, i.e. 0.03px = 0.004% of the window, which
// drags nowhere.
export const OVERFLOW_THRESHOLD_FRACTION = 0.002;

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/** 4 decimals — stable JSON, no float noise in the publicData diff. */
export const round4 = value => Math.round(value * 10000) / 10000;

/**
 * 🔴 12.13 AC-4 — the target guard, and the ONLY thing that makes the
 * wrong-target trap fail loudly.
 *
 * A missing or invalid target is a PROGRAMMING error: it means a Step 3 caller
 * forgot to thread `config.layout.listingImage` through, and the old silent
 * behaviour (`null` → a bland `{x: 50, y: 50}`) produced a plausible-looking
 * centre that is indistinguishable from a correct answer. The tph-api twin
 * `computeCoverCrop` already throws with a named message on a non-finite or
 * non-positive target; this matches it exactly.
 *
 * Deliberately NOT applied to sources — see the module header.
 *
 * @param {string} fnName the caller, so the message names the real site
 */
const assertTarget = (fnName, targetWidth, targetHeight) => {
  if (
    !isFiniteNumber(targetWidth) ||
    !isFiniteNumber(targetHeight) ||
    targetWidth <= 0 ||
    targetHeight <= 0
  ) {
    throw new Error(
      `${fnName}: targetWidth/targetHeight must be positive finite numbers ` +
        `(got ${targetWidth}x${targetHeight})`
    );
  }
};

/**
 * Coerce a raw `zoom` to a usable cover-scale multiplier (AC-5 / AC-8).
 *
 * Absent, non-numeric, NaN, Infinity or BELOW 1 all degrade to 1. A bad zoom
 * NEVER rejects the whole entry — a bad zoom is still a usable picture,
 * whereas a bad `x`/`y` is not (that still rejects to `null`, preserving
 * 15.8's asymmetry exactly).
 *
 * The below-1 floor is structural, not cosmetic: a zoom under 1 would scale
 * the source SMALLER than the target and break the cover guarantee on both
 * sides of the contract.
 *
 * Shared by every reader so the absent-zoom-is-1 rule is one implementation,
 * applied identically at every comparison, fallback and canonicalization.
 * Mirrors tph-api `focal.js#resolveZoom` (minus its corrupt-data ceiling,
 * which is a renderer-side backstop against data this module cannot write).
 */
export const resolveZoom = value => (isFiniteNumber(value) && value >= NO_ZOOM ? value : NO_ZOOM);

/**
 * Reader-side normalization, deliberately IDENTICAL in semantics to tph-api's
 * `adTemplateRenderer/focal.js#normalizeFocalPoint`: a value that is not two
 * finite numbers already within [0,1] is REJECTED (→ null), not clamped. The
 * preview must degrade exactly the way the renderer degrades, or the seller
 * sees a frame the ad will not reproduce.
 *
 * 🔴 12.13 AC-5 — the return is ALWAYS THREE KEYS when it is not `null`. A
 * legacy 15.8 entry (`{x, y}`, which is what every staging entry actually
 * looks like) comes back as `{x, y, zoom: 1}`, so the two stored forms are
 * INDISTINGUISHABLE downstream and every comparison is total. This is stated
 * as a property of the NORMALIZER rather than a list of call-sites so it holds
 * for consumers nobody has thought of yet.
 *
 * @param {*} value candidate focal point
 * @returns {{x: number, y: number, zoom: number}|null}
 */
export const normalizeStoredFocalPoint = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const { x, y } = value;
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
    return null;
  }
  if (x < 0 || x > 1 || y < 0 || y > 1) {
    return null;
  }
  return { x, y, zoom: resolveZoom(value.zoom) };
};

/**
 * Writer-side normalization. Fresh values derived from a drag ARE clamped —
 * they come from measured geometry, so a hair outside [0,1] is float noise, not
 * a bad value — then rounded to 4dp. Non-finite input yields null (nothing is
 * written) rather than a fabricated centre.
 *
 * 🔴 12.13 — the third argument is the zoom, and it is where LEG 1 of the
 * operator's defence-in-depth decision lives: the value is clamped to
 * `[1, maxZoom]` BEFORE it is ever stored, with `maxZoom` derived per photo
 * from real pixel dimensions (`maxZoomForSource`). This is the PRODUCT bound —
 * the only bound a seller can ever encounter. tph-api's `ZOOM_CEILING` sits
 * ~33x above it and is a corrupt-data backstop, not a second product cap.
 *
 * `maxZoom` defaults to `MAX_ZOOM_CAP` so a caller that has not resolved a
 * per-photo ceiling still cannot write an unbounded value; it is not a target
 * dimension, so it does not throw.
 *
 * @param {number} x
 * @param {number} y
 * @param {number} [zoom] raw zoom; absent/invalid ⇒ 1
 * @param {number} [maxZoom] the per-photo ceiling from `maxZoomForSource`
 * @returns {{x: number, y: number, zoom: number}|null}
 */
export const toFocalPoint = (x, y, zoom, maxZoom = MAX_ZOOM_CAP) => {
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
    return null;
  }
  const ceiling = isFiniteNumber(maxZoom) && maxZoom >= NO_ZOOM ? maxZoom : NO_ZOOM;
  return {
    x: round4(clamp(x, 0, 1)),
    y: round4(clamp(y, 0, 1)),
    zoom: round4(clamp(resolveZoom(zoom), NO_ZOOM, ceiling)),
  };
};

/**
 * True iff the value is exactly what the renderer will accept: a plain object
 * of finite numbers in [0,1] carrying no more than 4 decimal places, plus a
 * zoom that is a finite number >= 1 at no more than 4 dp. Used by the shape
 * test to pin the cross-repo contract; a string axis, a NaN, an out-of-range
 * value or a 17-decimal float all fail it.
 *
 * 🔴 12.13 AC-8 — this MUST see the zoom. A zoom-less pin cannot observe the
 * zoom being stripped by the prune, which is the highest-risk defect in this
 * story: it fails with nothing red anywhere.
 *
 * ⚠️ A value whose `zoom` key is ABSENT is still a legal stored shape (AC-8's
 * prune rule preserves it), and `normalizeStoredFocalPoint` resolves it to 1 —
 * so it passes. What must fail is a value whose zoom is PRESENT and garbage
 * carried through verbatim, which the normalizer prevents by construction.
 */
export const pinnedFocalShape = value => {
  const normalized = normalizeStoredFocalPoint(value);
  if (!normalized) {
    return false;
  }
  return (
    round4(normalized.x) === normalized.x &&
    round4(normalized.y) === normalized.y &&
    isFiniteNumber(normalized.zoom) &&
    normalized.zoom >= NO_ZOOM &&
    round4(normalized.zoom) === normalized.zoom
  );
};

/**
 * The map key is ALWAYS the real image uuid string.
 *
 * `values.images` mixes saved images (`id` is a UUID object) with just-uploaded
 * ones (`id` is a temp STRING and the real uuid lives on `imageId`) — see
 * EditListingPage.duck.js. An entry with no resolvable uuid is skipped, never
 * keyed by a temp id.
 *
 * @returns {string|null}
 */
export const imageUuid = image => {
  if (!image) {
    return null;
  }
  const resolved = typeof image.id === 'string' ? image.imageId?.uuid : image.id?.uuid;
  return typeof resolved === 'string' && resolved.length > 0 ? resolved : null;
};

/**
 * A stable identity for an image ACROSS re-renders, including one that is still
 * uploading. Unlike `imageUuid` this deliberately accepts the temp string id,
 * because the caller is tracking form-state identity rather than minting a
 * storage key — a temp id must never key `photoFocalPoints`, but it is a
 * perfectly good handle for "which image is this, in this session".
 *
 * @returns {string|null}
 */
export const imageOrderKey = image => {
  const uuid = imageUuid(image);
  if (uuid) {
    return uuid;
  }
  return typeof image?.id === 'string' && image.id.length > 0 ? image.id : null;
};

/** The cover IS `images[0]` — there is no separate cover field. */
export const coverImageUuid = images =>
  Array.isArray(images) && images.length > 0 ? imageUuid(images[0]) : null;

/**
 * Read a photo's stored focal, defaulting to centre. Orphaned and garbage
 * entries read as centre (leak defence (b) — image ids are minted fresh on
 * every re-upload, so orphans are structural).
 *
 * 🔴 12.13 — the return is always THREE keys, because both branches are: the
 * sentinel carries `zoom: 1` and the normalizer always resolves one. This is
 * what keeps `focalToInitialCroppedAreaPercentages` off the NaN path in the
 * default state of every photo on every draft.
 *
 * @returns {{x: number, y: number, zoom: number}} never null
 */
export const readFocalPoint = (focalPoints, imageId) => {
  if (!focalPoints || typeof focalPoints !== 'object' || !imageId) {
    return CENTRE_FOCAL_POINT;
  }
  return normalizeStoredFocalPoint(focalPoints[imageId]) || CENTRE_FOCAL_POINT;
};

/**
 * Leak defence (a) — prune on save. Keeps only entries whose uuid is present in
 * the current `images`, and re-normalizes every surviving value so a garbage
 * entry can never be written back out.
 *
 * 🔴 12.13 AC-8 — THIS IS THE HIGHEST-RISK FUNCTION IN THE STORY. It
 * re-normalizes every survivor on EVERY save, from EVERY save path. A
 * normalizer that returned `{x, y}` would destroy the zoom the first time the
 * seller pressed Next — and nothing would fail, anywhere; the feature would
 * ship inert. The round-trip test is the only thing that catches it.
 *
 * 🔴 AND IT PRESERVES AN ABSENT ZOOM RATHER THAN MATERIALIZING ONE
 * (operator-locked). If this normalized `{x, y}` up to `{x, y, zoom: 1}` then
 * the FIRST SAVE ON ANY LISTING would rewrite every entry 15.8 already wrote,
 * churning `publicData` on listings nobody framed — which is exactly what
 * AC-5's "zoom is never written eagerly" forbids. So:
 *
 *   `{x, y, zoom}` in ⇒ `{x, y, zoom}` out, byte-identical;
 *   `{x, y}`       in ⇒ `{x, y}`       out, STILL TWO KEYS;
 *
 * and READERS default the absent zoom to 1, so the two forms are
 * indistinguishable downstream. Storage stays minimal; semantics stay total.
 *
 * ⚠️ This does not conflict with the known empty-map write (15.8 review N-15):
 * "never written eagerly" governs `{x: 0.5, y: 0.5}` ENTRIES, not the empty
 * map, which is harmless (the key is outside tph-api's 44-key allowlist and
 * the renderer reads `publicData.photoFocalPoints || {}`).
 *
 * @returns {Object} a new map (the input is never mutated)
 */
export const pruneFocalPoints = (focalPoints, images) => {
  if (!focalPoints || typeof focalPoints !== 'object' || Array.isArray(focalPoints)) {
    return {};
  }
  const liveIds = new Set(
    (Array.isArray(images) ? images : []).map(imageUuid).filter(id => id !== null)
  );
  return Object.keys(focalPoints).reduce((acc, id) => {
    if (!liveIds.has(id)) {
      return acc;
    }
    const raw = focalPoints[id];
    const normalized = normalizeStoredFocalPoint(raw);
    if (!normalized) {
      return acc;
    }
    // Preserve, do not materialize. Only an entry that ALREADY carries a zoom
    // keeps one — and a garbage one is normalized to 1 rather than written back
    // out, which is the prune's whole purpose. An entry with NO zoom stays two
    // keys. `raw` is a plain object here (the normalizer rejected everything
    // else), so reading `.zoom` is safe.
    acc[id] = raw.zoom === undefined ? { x: normalized.x, y: normalized.y } : normalized;
    return acc;
  }, {});
};

/**
 * Cover-scaled source dimensions. The GOVERNING axis lands EXACTLY on its
 * target (computed as an assignment, not as `W * scale`) so the degenerate-axis
 * test below is exact rather than epsilon-dependent.
 *
 * 🔴 12.13 AC-4 — the target is REQUIRED and a bad one THROWS. A bad SOURCE
 * still returns `null`.
 */
export const coverScaledSize = ({ sourceWidth, sourceHeight, targetWidth, targetHeight }) => {
  assertTarget('coverScaledSize', targetWidth, targetHeight);
  if (
    !isFiniteNumber(sourceWidth) ||
    !isFiniteNumber(sourceHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return null;
  }
  const scaleX = targetWidth / sourceWidth;
  const scaleY = targetHeight / sourceHeight;
  return scaleX >= scaleY
    ? { scaledWidth: targetWidth, scaledHeight: sourceHeight * scaleX }
    : { scaledWidth: sourceWidth * scaleY, scaledHeight: targetHeight };
};

/**
 * 🔴 12.13 AC-6 — the PER-PHOTO zoom ceiling, so a zoomed crop can never
 * render soft.
 *
 *   coverScale = max(targetWidth / sourceWidth, targetHeight / sourceHeight)
 *   maxZoom    = clamp(1 / coverScale, 1, cap)
 *
 * i.e. zoom in only as far as the sampled source region is still at least the
 * target's pixel size.
 *
 * Callers pass the LOWER of the Step 3 4:3 target and the ad band — see
 * `maxZoomForPhoto`. When `maxZoom === 1` the zoom control is DISABLED, not
 * hidden: a small photo that cannot be zoomed must not look like a broken
 * control. A source already smaller than the target on its binding axis yields
 * `coverScale >= 1` ⇒ `maxZoom = 1`, which is correct and is the common case
 * for a small legacy upload.
 *
 * 🔴 With NO USABLE SOURCE DIMENSIONS this returns `1` rather than throwing. A
 * just-uploaded photo can briefly have none of `scaled-xlarge` / `scaled-large`
 * / `default`; returning 1 disables the control rather than leaving it
 * unbounded, and it re-enables itself when the variant lands. This is a
 * SOURCE-side degradation, not a target-side one — see AC-4's split.
 *
 * @returns {number} in [1, cap]
 */
export const maxZoomForSource = ({
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
  cap = MAX_ZOOM_CAP,
}) => {
  assertTarget('maxZoomForSource', targetWidth, targetHeight);
  if (
    !isFiniteNumber(sourceWidth) ||
    !isFiniteNumber(sourceHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return NO_ZOOM;
  }
  const coverScale = Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight);
  return clamp(1 / coverScale, NO_ZOOM, cap);
};

/**
 * The ceiling a Step 3 caller actually wants: the LOWER of the listing's own
 * target and the ad band.
 *
 * 🔴 Rationale, and it is not tidiness: the SAME stored zoom is consumed by the
 * renderer against the 980 x 742 band (transitionally, until story 12-14 gives
 * the ad its own key). A zoom that is safe at 4:3 but soft at 1.321 would ship
 * a soft ad. One number, safe for both consumers.
 *
 * ⚠️ HAND-OFF TO 12-14: once the ad has its own framing key, the listing focal
 * is no longer consumed by the ad and the ad-band half of this ceiling
 * permanently constrains the listing zoom for no reason. It fails safe (the
 * seller can zoom slightly less than they could), so it is a relaxable
 * follow-up, not a defect — but it should be relaxed there rather than
 * surviving as folklore.
 */
export const maxZoomForPhoto = ({
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
  cap = MAX_ZOOM_CAP,
}) =>
  Math.min(
    maxZoomForSource({ sourceWidth, sourceHeight, targetWidth, targetHeight, cap }),
    maxZoomForSource({
      sourceWidth,
      sourceHeight,
      targetWidth: AD_BAND_WIDTH,
      targetHeight: AD_BAND_HEIGHT,
      cap,
    })
  );

/**
 * ⚠️ CORRECTION, 2026-08-15 (story 12.14 cold code review, NOTE-10) — READ THIS
 * BEFORE ACTING ON `d5b3a84d9`'s "NOT FIXED, REPORTED ONLY" SECTION.
 *
 * That commit message records: *"tph-api's ad renderer shares the seam.
 * `photoBand.js:98` takes `sharp(source).metadata()`, which is stored-space …
 * Latent only — 0 production covers are transposed."* **That conclusion is
 * WRONG and must not be inherited.** It was proven on a SYNTHETIC
 * orientation-6 JPEG — which establishes what `sharp` does with a TAGGED file,
 * and was then generalised to production bytes that are not tagged. It was
 * never tested against a delivered variant.
 *
 * A read-only probe over **799 production images** (2026-08-15, full evidence
 * in `_bmad-output/implementation-artifacts/12-14-cold-code-review-2026-08-14.md`
 * §8) settled it:
 *
 *   - **0 of 799** carry an EXIF orientation tag; `metadata.exif` is absent
 *     entirely. imgix bakes the rotation in and strips the block.
 *   - The two genuinely transposed photos (`6a7f11ec-2e7f-4375-8168-673e664df20c`,
 *     `6a76445d-8414-42ac-8d63-036cd7d8980c`) report 2400x1800 from the API and
 *     decode to 1800x2400; `sharp` reads **1800x2400**, and `.rotate()` on those
 *     bytes is a **no-op**.
 *   - The shipped `renderPhotoBandLayers` was executed on one of them and
 *     produced an upright, undistorted 980x742 band. Feeding the same pipeline
 *     the stored-space pair instead produced the visibly squashed counterfactual.
 *
 * So the paragraph BELOW is the half of `d5b3a84d9` that is right: the bytes are
 * true and it is the **API's variant metadata** that is stale. Server-side,
 * `sharp` already reads true visual orientation.
 *
 * 🔴 **DO NOT ADD `.rotate()` / `autoOrient` TO tph-api.** It is a no-op on
 * tag-free pre-rotated bytes today, and it would become a silent
 * DOUBLE-ROTATION the day Sharetribe changed its ingest or delivery pipeline —
 * load-bearing code that looks like a safety net. The orientation defect is
 * browser-side ONLY, and this module is its fix.
 *
 * 🔴 DEFECT (staging, measured live 2026-08-14) — SHARETRIBE'S VARIANT METADATA
 * IS NOT THE SOURCE OF TRUTH ABOUT A PHOTO'S ORIENTATION.
 *
 * For a photo shot in portrait on a phone, the file is stored landscape with an
 * EXIF orientation flag. Sharetribe's variant `width`/`height` describe the
 * STORED buffer; every browser since Chrome 81 defaults to
 * `image-orientation: from-image`, decodes the flag, and reports the ROTATED
 * dimensions in `naturalWidth`/`naturalHeight`. The two are exact TRANSPOSES of
 * each other, and nothing in the metadata says so.
 *
 * That split is the whole of the framer↔preview disagreement, measured on the
 * cover photo of listing `6a6084f3-…` on staging:
 *
 *   react-easy-crop sizes the media from the DECODED image (its `computeSizes`
 *     reads `naturalWidth`/`naturalHeight`) → 1800 x 2400, aspect 0.75
 *   `framedImageStyle` was handed the VARIANT METADATA                → 2400 x 1800, aspect 1.333
 *
 *   framer  visible source region   x [0.1703, 0.6716]   y [0.1015, 0.3835]
 *   preview visible source region   x [0.1700, 0.6720]   y [-0.0019, 0.5010]
 *
 * Horizontal agrees to three decimals; the vertical extent is inflated by
 * 1.783x, which is (4/3) / (3/4) — the transpose factor exactly.
 *
 * 🔴 AND IT IS NOT ONLY A PAN ERROR. `framedImageStyle` sets width AND height,
 * and the framed classes carry `object-fit: fill`, so a wrong source ASPECT
 * DISTORTS the photograph. Read off the live listing page for the same image:
 * natural aspect 0.75 rendered into a box at aspect 1.333 — a portrait photo
 * squashed to 56 % of its height on the public listing page. The "stretch on a
 * portrait phone photo" the operator reported earlier was blamed on the
 * since-replaced percentage-of-a-measured-box preview; it was ALWAYS this, and
 * it survived that rewrite untouched.
 *
 * The decoded image wins, always, because it is what actually gets painted.
 *
 * ⚠️ The decoded size may come from a DIFFERENT variant than the metadata (the
 * gallery measures whichever `scaled-*` the hero displays). That is safe for
 * `framedImageStyle`, which is SCALE-INVARIANT — multiplying the source by k
 * divides `coverScale` by k and leaves every returned percentage unchanged, so
 * only the ASPECT it carries is load-bearing, and every `scaled-*` derivative
 * of one photo shares it. It is NOT safe for `maxZoomForPhoto`, which is a
 * sharpness ceiling and genuinely needs that variant's own pixel count — pass
 * it the decoded size of the variant you are actually FRAMING.
 *
 * @returns {{sourceWidth: number, sourceHeight: number}} the variant's own
 *   numbers when nothing has been decoded yet, so the caller's pre-load
 *   behaviour is unchanged.
 */
export const resolveFramingSource = ({
  variantWidth,
  variantHeight,
  decodedWidth,
  decodedHeight,
}) => {
  const decodedUsable =
    isFiniteNumber(decodedWidth) &&
    isFiniteNumber(decodedHeight) &&
    decodedWidth > 0 &&
    decodedHeight > 0;
  return decodedUsable
    ? { sourceWidth: decodedWidth, sourceHeight: decodedHeight }
    : { sourceWidth: variantWidth, sourceHeight: variantHeight };
};

/**
 * 🔴 12.13 AC-17 — the framed geometry, and it is THE SAME MATH THE RENDERER
 * RUNS.
 *
 * Produces the style for an absolutely-positioned `<img>` inside an
 * `overflow: hidden` box of the target's shape, computed from the same
 * `scale / scaledWidth / scaledHeight / left / top` sequence as
 * `tph-api/src/services/adTemplateRenderer/focal.js#computeCoverCrop`,
 * expressed as percentages of the target instead of pixels.
 *
 *   scale = max(tw/W, th/H) * zoom
 *   left/top place the target-sized rectangle so its CENTRE sits at the focal,
 *   clamped to the scaled bounds.
 *
 * 🔴 DO NOT ROUND. `computeCoverCrop` rounds to integers because
 * `sharp.extract()` demands integer pixels; CSS does not, and rounding against
 * a DIFFERENT target would introduce error the renderer does not have. Parity
 * is "the same rectangle to sub-pixel" — that is the honest claim and it is
 * what the test asserts.
 *
 * ONE HELPER, FIVE CONSUMERS: the framing tool's static fallback, the wizard
 * tiles, the Step 3 card preview, the listing-page gallery hero and — since
 * story 12.15 — THE LISTING CARD, on every one of the ten surfaces that renders
 * one (`util/cardFraming.js`). A second implementation anywhere is a review
 * failure.
 *
 * Throws on a missing/invalid target (AC-4); a bad source returns `null` so
 * the caller can fall back to its unframed behaviour.
 *
 * @returns {{width: string, height: string, left: string, top: string}|null}
 */
export const framedImageStyle = ({
  focal,
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
}) => {
  assertTarget('framedImageStyle', targetWidth, targetHeight);
  if (
    !isFiniteNumber(sourceWidth) ||
    !isFiniteNumber(sourceHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return null;
  }
  const point = normalizeStoredFocalPoint(focal) || CENTRE_FOCAL_POINT;
  const coverScale = Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight);
  const scale = coverScale * point.zoom;
  const scaledWidth = sourceWidth * scale;
  const scaledHeight = sourceHeight * scale;
  // Same clamp as the renderer's `restrictPosition` mirror: the photo can never
  // be dragged past its own edges. `Math.max(…, 0)` guards the degenerate axis,
  // where `scaled - target` is exactly 0 under cover-fit at zoom 1.
  const left = clamp(
    point.x * scaledWidth - targetWidth / 2,
    0,
    Math.max(scaledWidth - targetWidth, 0)
  );
  const top = clamp(
    point.y * scaledHeight - targetHeight / 2,
    0,
    Math.max(scaledHeight - targetHeight, 0)
  );
  return {
    width: `${(scaledWidth / targetWidth) * 100}%`,
    height: `${(scaledHeight / targetHeight) * 100}%`,
    left: `${(-left / targetWidth) * 100}%`,
    top: `${(-top / targetHeight) * 100}%`,
  };
};

/**
 * 🔴 Story 12.15 (AC-6) — THE `transform-origin` THAT KEEPS A HOVER ZOOM FROM
 * UN-FRAMING THE PHOTO.
 *
 * A listing card scales its `<img>` by 1.03 on hover
 * (`ListingCard.module.css`). Today that is invisible: the unframed `<img>` box
 * EQUALS the card box, so scaling about its own centre is symmetric.
 *
 * Once framed the `<img>` is LARGER than the box and OFFSET inside it, so
 * `scale()` about the element's own centre DRIFTS the visible region — a photo
 * framed toward the top slides down on every mouseover, partially undoing the
 * seller's framing. Small, and exactly the class of silent wrongness story
 * 12.15 exists to remove.
 *
 * DERIVATION (write the test from THIS, not from the code below — AC-14):
 *   the img spans target-units [L, L+W], where L = left%/100 (≤ 0) and
 *   W = width%/100 (≥ 1). The BOX CENTRE sits at target-unit 0.5. Its position
 *   AS A FRACTION OF THE IMG is therefore (0.5 − L) / W. Scaling about that
 *   point leaves it fixed.
 *
 * Both axes are in [0, 1]: containment (L + W ≥ 1 ≥ 0.5, from the clamp in
 * `framedImageStyle`) puts the box centre inside the element on both axes,
 * always. So the origin is never outside the element.
 *
 * `transform-origin` percentages resolve against the element's BORDER BOX —
 * this repo established that the hard way (`EditListingPhotosForm.js`'s
 * `object-position`-vs-`transform-origin` coordinate-system note), and it is
 * why the derivation above anchors the right thing.
 *
 * @param {{width: string, height: string, left: string, top: string}|null} style
 *   a `framedImageStyle` result
 * @returns {string|null} e.g. `"37.5% 21.25%"`, or `null` for an unframed card
 *   (which keeps the plain centre-origin scale it has today)
 */
export const framedTransformOrigin = style => {
  if (!style) {
    return null;
  }
  const parse = value => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n / 100 : null;
  };
  const left = parse(style.left);
  const top = parse(style.top);
  const width = parse(style.width);
  const height = parse(style.height);
  if (left === null || top === null || !width || !height) {
    return null;
  }
  const originX = ((0.5 - left) / width) * 100;
  const originY = ((0.5 - top) / height) * 100;
  return `${originX}% ${originY}%`;
};

/**
 * Static fallback (pre-mount and no-JS): the focal expressed as CSS
 * `object-position` percentages against `object-fit: cover`.
 *
 *   p = clamp((f * scaled - target / 2) / (scaled - target), 0, 1) * 100
 *
 * 🔴 The degenerate axis is the COMMON case, not an edge case. Under cover-fit
 * exactly one axis always has `scaled === target`, so that axis would evaluate
 * 0/0 = NaN. When the axis has no overflow the position is arbitrary and 50 %
 * is the honest answer.
 *
 * 🔴 TODO (story 12-14, Step 6) — `framedImageStyle` is this helper's
 * SUCCESSOR and Step 6 must switch to it or extend this one. This helper is
 * ZOOM-BLIND, and that is a stopgap, NOT a property of its remaining consumer:
 * the Step 6 ad backdrop consumes the SAME stored focal, and AC-23 applies the
 * zoom to BOTH the sharp band and the backdrop. It is acceptable here only
 * because this story does not render the ad preview. Describing it as
 * "zoom-free" would hand 12-14 a silent bug.
 *
 * Kept exported and tested because its degenerate-axis rule is genuinely the
 * common case. AC-4 applies: the target is required and throws.
 *
 * @returns {{x: number, y: number}} percentages in [0, 100]
 */
export const focalToObjectPosition = ({
  focal,
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
}) => {
  assertTarget('focalToObjectPosition', targetWidth, targetHeight);
  const size = coverScaledSize({ sourceWidth, sourceHeight, targetWidth, targetHeight });
  const point = normalizeStoredFocalPoint(focal) || CENTRE_FOCAL_POINT;
  if (!size) {
    return { x: 50, y: 50 };
  }
  const axis = (f, scaled, target) => {
    const overflow = scaled - target;
    if (overflow <= 0) {
      return 50;
    }
    return clamp((f * scaled - target / 2) / overflow, 0, 1) * 100;
  };
  return {
    x: axis(point.x, size.scaledWidth, targetWidth),
    y: axis(point.y, size.scaledHeight, targetHeight),
  };
};

/**
 * react-easy-crop restore contract (B-1). The cropper is CONTROLLED and mounts
 * at the origin unless it is given `initialCroppedAreaPercentages`; without
 * this the mount-time `onCropComplete` reports dead centre and would overwrite
 * the seller's stored framing.
 *
 * Returns the crop rectangle as PERCENTAGES OF THE SOURCE — the exact inverse
 * of the `croppedArea` → focal derivation, so
 * `focal → percentages → focal` round-trips.
 *
 * 🔴 12.13 AC-11(b) — THIS RESTORES POSITION ONLY. Both channels of the
 * controlled cropper carry position-and-scale information, and an unstated
 * split is exactly how 15.8's B-1 happened. The split is: this rectangle
 * restores POSITION; the controlled `zoom` prop restores ZOOM. The rectangle
 * is nevertheless computed AT the stored zoom — the crop rectangle genuinely
 * is smaller when zoomed in, and a rectangle computed at zoom 1 while the
 * controlled prop said 2 would put the two channels in disagreement.
 *
 * @returns {{x: number, y: number, width: number, height: number}|null}
 */
export const focalToInitialCroppedAreaPercentages = ({
  focal,
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
}) => {
  assertTarget('focalToInitialCroppedAreaPercentages', targetWidth, targetHeight);
  const size = coverScaledSize({ sourceWidth, sourceHeight, targetWidth, targetHeight });
  if (!size) {
    return null;
  }
  const point = normalizeStoredFocalPoint(focal) || CENTRE_FOCAL_POINT;
  // The crop rectangle shrinks as the zoom rises: at zoom z the window samples
  // `1/z` of the cover-scaled extent on each axis.
  const width = (targetWidth / size.scaledWidth / point.zoom) * 100;
  const height = (targetHeight / size.scaledHeight / point.zoom) * 100;
  return {
    x: clamp(point.x * 100 - width / 2, 0, 100 - width),
    y: clamp(point.y * 100 - height / 2, 0, 100 - height),
    width,
    height,
  };
};

/**
 * The inverse: the focal point a `croppedArea` (react-easy-crop's PERCENTAGE
 * first argument to `onCropComplete`) represents. Clamped and rounded here, so
 * this is the single place a stored value is minted.
 *
 * 🔴 12.13 AC-11(a) — TWO CHANNELS, ON PURPOSE, AND THIS IS THE ONE MOST
 * LIKELY TO BE BROKEN BY THE NATURAL IMPLEMENTATION.
 *
 *   POSITION comes from `croppedArea` — NEVER from the library's internal
 *   transform state. Do not read its crop/translate values, and do not
 *   back-compute position from the zoom.
 *
 *   ZOOM does NOT come from `croppedArea`. It is react-easy-crop's controlled
 *   `zoom` prop, which the framer owns as component state alongside `crop`,
 *   and it is passed in here as the second argument. There is exactly one zoom
 *   value and one owner; the zoom row, the floating buttons and pinch all
 *   write the same state.
 *
 * @param {object} croppedArea react-easy-crop's percentage rectangle
 * @param {number} [zoom] the controlled zoom state
 * @param {number} [maxZoom] the per-photo ceiling (clamp on write — AC-6)
 * @returns {{x: number, y: number, zoom: number}|null}
 */
export const croppedAreaToFocalPoint = (croppedArea, zoom, maxZoom) => {
  if (!croppedArea || typeof croppedArea !== 'object') {
    return null;
  }
  const { x, y, width, height } = croppedArea;
  if (
    !isFiniteNumber(x) ||
    !isFiniteNumber(y) ||
    !isFiniteNumber(width) ||
    !isFiniteNumber(height)
  ) {
    return null;
  }
  return toFocalPoint((x + width / 2) / 100, (y + height / 2) / 100, zoom, maxZoom);
};

/**
 * Does the photo overflow the window enough to be worth dragging?
 *
 * Computed from the SOURCE ASPECT RATIO alone against the caller's target, so
 * the answer does not depend on which variant the preview happens to be
 * displaying.
 *
 * 🔴 12.13 AC-11 — now recomputed AT THE CURRENT ZOOM. At `zoom > 1` a photo
 * always overflows, so the gate is only ever interesting at zoom 1; passing
 * the zoom keeps the hint and reticle honest rather than making them a
 * special case at the call site.
 */
export const photoOverflowsWindow = ({
  sourceWidth,
  sourceHeight,
  targetWidth,
  targetHeight,
  zoom,
}) => {
  assertTarget('photoOverflowsWindow', targetWidth, targetHeight);
  const size = coverScaledSize({ sourceWidth, sourceHeight, targetWidth, targetHeight });
  if (!size) {
    return false;
  }
  const z = resolveZoom(zoom);
  // Each axis is normalised against ITS OWN target extent, so the comparison is
  // unit-free: 980 x 742 and 4 x 3 describing the same window give the same
  // answer. Taking the max means "overflows on either axis".
  const overflow = Math.max(
    (size.scaledWidth * z - targetWidth) / targetWidth,
    (size.scaledHeight * z - targetHeight) / targetHeight
  );
  return overflow >= OVERFLOW_THRESHOLD_FRACTION;
};

/**
 * The broodmare ribbon is CONDITIONAL, and the condition is mirrored from the
 * renderer's own predicate rather than re-invented:
 * `tph-api/src/services/adTemplateRenderer/layout.js#isBroodmare` reads
 * `String(data.sex).trim().toLowerCase() === 'broodmare'`, and `data.sex` is a
 * straight passthrough of `publicData.sex` (CONTRACT_PASSTHROUGH_KEYS).
 *
 * (AC-6 named broodmareStatus/mareBredTo/foalBirthYear/foalBirthMonth; those
 * feed the ribbon's TEXT, not the ribbon's presence. The AC's own instruction —
 * "read the predicate at its definition and mirror it" — governs.)
 */
export const showsBroodmareRibbon = publicData =>
  String(publicData?.sex || '')
    .trim()
    .toLowerCase() === 'broodmare';
